import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, DollarSign, Clock, Users, TrendingUp, Star } from 'lucide-react';
import { venueAnalyticsService, VenueAnalytics } from '@/services/venueAnalyticsService';

interface VenueAnalyticsDashboardProps {
  venueId: string;
}

export const VenueAnalyticsDashboard: React.FC<VenueAnalyticsDashboardProps> = ({ venueId }) => {
  const [analytics, setAnalytics] = useState<VenueAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [venueId]);

  const loadAnalytics = async () => {
    try {
      const data = await venueAnalyticsService.getVenueAnalytics(venueId);
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
            <CardContent className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-muted rounded w-24 mb-2"></div>
                <div className="h-8 bg-muted rounded w-16"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!analytics) {
    return (
      <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">Failed to load analytics data</p>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization >= 80) return 'text-green-500';
    if (utilization >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="space-y-6">
      {/* Top Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Venues</p>
                <p className="text-3xl font-bold text-green-500">{analytics.activeVenues}</p>
                <p className="text-xs text-muted-foreground">locations</p>
              </div>
              <MapPin className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Today's Bookings</p>
                <p className="text-3xl font-bold text-blue-500">{analytics.todaysBookings}</p>
                <p className="text-xs text-muted-foreground">scheduled</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Monthly Revenue</p>
                <p className="text-3xl font-bold text-green-500">{formatCurrency(analytics.monthlyRevenue)}</p>
                <p className="text-xs text-muted-foreground">this month</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schedule and Management Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Schedule */}
        <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Today's Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {analytics.todaysSchedule.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No events scheduled for today</p>
            ) : (
              analytics.todaysSchedule.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                  <div>
                    <p className="font-medium">{item.field}</p>
                    <p className="text-sm text-muted-foreground">{item.teams}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-500">{item.time}</p>
                    <Badge variant={item.type === 'game' ? 'default' : 'secondary'}>
                      {item.type}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Venue Management */}
        <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Venue Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {analytics.venueUtilization.map((venue, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{venue.name}</h4>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${getUtilizationColor(venue.utilization)}`}>
                      {venue.utilization}%
                    </span>
                    {venue.status && (
                      <Badge variant="destructive">{venue.status}</Badge>
                    )}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  Utilization
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Customer Analytics */}
      <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Customer Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-muted/20 rounded-lg">
              <p className="text-3xl font-bold text-green-500">{analytics.customerAnalytics.regularCustomers}</p>
              <p className="text-sm text-muted-foreground">Regular Customers</p>
            </div>
            <div className="text-center p-4 bg-muted/20 rounded-lg">
              <p className="text-3xl font-bold text-green-500">{analytics.customerAnalytics.satisfactionRate}%</p>
              <p className="text-sm text-muted-foreground">Satisfaction Rate</p>
            </div>
            <div className="text-center p-4 bg-muted/20 rounded-lg">
              <div className="flex items-center justify-center gap-1">
                <p className="text-3xl font-bold text-green-500">{analytics.customerAnalytics.averageRating}</p>
                <Star className="h-6 w-6 text-yellow-500 fill-current" />
              </div>
              <p className="text-sm text-muted-foreground">Average Rating</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};